/**
 * Assistant Chat Component
 *
 * Chat interface for the Assistant agent.
 * Built with first-principles chat UX:
 * - Always scroll to bottom on new messages
 * - "Scroll to bottom" button when user scrolls up
 * - Auto-focus input after agent responds
 * - Enter to send, Shift+Enter for new line
 */

import {
  IconArrowDown,
  IconArrowsMaximize,
  IconArrowUp,
  IconCheck,
  IconChevronRight,
  IconFile,
  IconPaperclip,
  IconPlayerStopFilled,
  IconRefresh,
  IconSparkles,
  IconThumbDown,
  IconThumbUp,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { AgentActivityTrace, AgentThinkingLine } from "~/components/domain/ai/AgentActivityTrace";
import { CHAT_TYPE } from "~/components/domain/ai/chat-typography";
import type { AssistantDraftAttachment } from "~/contexts/assistant-conversation-store";
import type { ActionDraftContext, ActionIntent } from "~/shared/ai/assistant-action-artifact";
import { ASSISTANT_ATTACHMENT_MAX_FILE_BYTES } from "~/shared/ai/assistant-attachment-contract";
import type { AssistantEntrySurface } from "~/shared/ai/assistant-entry-surface";
import { appendVoiceTranscript } from "~/shared/voice/browser-voice-capture";
import { BLUE_PALETTE, DANGER_PALETTE, GRAY_PALETTE } from "~/styles/palettes";
import {
  ActionIcon,
  Box,
  Button,
  Collapse,
  Dropzone,
  Flex,
  Image,
  Paper,
  ScrollArea,
  Text,
  Textarea,
  ThemeIcon,
  Transition,
  UnstyledButton,
  useElementSize,
  useForm,
  zodResolver,
} from "~/ui";
import { ActionProposalCard } from "./ActionProposalCard";
import { AssistantArtifactCard } from "./AssistantArtifactCard";
import assistantStyles from "./AssistantChat.module.css";
import { AssistantInlineResult, inlineGridContentWidth, shouldRenderArtifactInline } from "./AssistantInlineResult";
import { AssistantMessageBody } from "./AssistantMessageBody";
import { AssistantVoiceInput } from "./AssistantVoiceInput";
import { type ActionArtifactState, type ActionSelectionPatch, emptyActionArtifactState } from "./action-artifact-state";
import { useActionPreviewItems } from "./action-preview";
import { ASSISTANT_ACTION_COPY } from "./assistant-action-copy";
import {
  type AssistantFeedback,
  type ChatMessage,
  dwellGatedTickerLabel,
  formatWorkSummary,
  initialQueryDisposition,
  latestThoughtTicker,
  type MessageWork,
  nextAssistantFeedback,
  resolveLiveTurnFrontier,
  splitTurnSegmentsAroundContent,
  type TurnSegment,
} from "./assistant-chat-state";
import { assistantVoiceEntityRefs } from "./assistant-voice-entity-refs";
import { CopyAnswerButton } from "./CopyAnswerButton";
import { type ReportArtifactState, reportArtifactEntry, reportCommitRejectionCopy, visibleDisposition } from "./report-artifact-state";

const chatFormSchema = z.object({
  message: z.string().trim(),
});
type ChatFormType = z.infer<typeof chatFormSchema>;

const formatAttachmentSize = (sizeBytes: number): string =>
  sizeBytes >= 1024 * 1024 ? `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.ceil(sizeBytes / 1024))} KB`;

const attachmentKind = (fileName: string): string => {
  const extension = fileName.toLowerCase().split(".").pop();
  if (["png", "jpg", "jpeg", "webp"].includes(extension ?? "")) {
    return "Image";
  }
  return extension?.toUpperCase() ?? "File";
};

const attachmentColors = {
  "--attachment-blue": BLUE_PALETTE[6],
  "--attachment-light": BLUE_PALETTE[0],
  "--attachment-border": GRAY_PALETTE[3],
};

const attachmentErrorLabel = (error?: string): string => {
  if (error?.startsWith("Save this file as PDF")) {
    return "Save as PDF";
  }
  if (error?.startsWith("Save this spreadsheet as .xlsx")) {
    return "Save as .xlsx";
  }
  return error?.startsWith("Use PDF,") ? "Unsupported file type" : (error ?? "Upload failed");
};

const AttachmentChip = ({
  fileName,
  sizeBytes,
  status,
  error,
  retryable = true,
  onRetry,
  onRemove,
  previewUrl,
}: {
  fileName: string;
  sizeBytes: number;
  status?: AssistantDraftAttachment["status"];
  error?: string;
  retryable?: boolean;
  onRetry?: () => void;
  onRemove?: () => void;
  previewUrl?: string;
}) => (
  <Paper className={assistantStyles.attachmentChip} radius={8} p={6} bg="white" style={{ ...attachmentColors, minWidth: 0 }}>
    <Flex align="center" gap={7}>
      {previewUrl ? (
        <Box className={assistantStyles.attachmentPreview}>
          <Image src={previewUrl} alt="" w={30} h={30} radius={7} fit="cover" />
          {status === "attached" && (
            <Flex className={assistantStyles.attachmentPreviewStatus} align="center" justify="center" aria-label="Attached">
              <IconCheck size={9} />
            </Flex>
          )}
        </Box>
      ) : (
        <Flex
          className={assistantStyles.attachmentIcon}
          align="center"
          justify="center"
          style={status === "failed" ? { color: DANGER_PALETTE[7], background: DANGER_PALETTE[0] } : undefined}
        >
          {status === "failed" ? <IconX size={16} /> : <IconFile size={16} />}
        </Flex>
      )}
      <Box miw={0} style={{ flex: 1 }}>
        <Text size="xs" fw={600} truncate title={fileName}>
          {fileName}
        </Text>
        <Text size="xs" c={status === "failed" ? DANGER_PALETTE[7] : GRAY_PALETTE[6]} truncate title={error} aria-label={error}>
          {status === "uploading"
            ? "Uploading…"
            : error
              ? attachmentErrorLabel(error)
              : `${attachmentKind(fileName)} · ${formatAttachmentSize(sizeBytes)}`}
        </Text>
        {status === "uploading" && <Box className={assistantStyles.attachmentProgress} />}
      </Box>
      {status === "failed" && retryable && onRetry && (
        <ActionIcon
          className={assistantStyles.mobileTouchTarget}
          size="sm"
          variant="subtle"
          aria-label={`Retry ${fileName}`}
          onClick={onRetry}
        >
          <IconRefresh size={14} />
        </ActionIcon>
      )}
      {onRemove && (
        <ActionIcon
          className={assistantStyles.mobileTouchTarget}
          size="sm"
          variant="subtle"
          color="gray"
          aria-label={`Remove ${fileName}`}
          onClick={onRemove}
        >
          <IconX size={14} />
        </ActionIcon>
      )}
    </Flex>
  </Paper>
);

export const shouldLockAssistantDraft = (isStreaming: boolean, isLoading: boolean, isVoiceBusy: boolean): boolean =>
  isStreaming || isLoading || isVoiceBusy;

interface AssistantChatProps {
  initialQuery?: string;
  sendInitialQuery?: boolean;
  onInitialQueryConsumed?: () => void;
  surface: AssistantEntrySurface;
  artifactState: ReportArtifactState;
  actionState?: ActionArtifactState;
  /** The family of the review open in the side pane, whose inline card then shows no controls. */
  panelActionFamilyId?: string | null;
  onOpenArtifact: (artifactKey: string) => void;
  onOpenAction?: (proposalId: string) => void;
  onStageAction?: (proposalId: string, patch: ActionSelectionPatch) => void;
  messages: ChatMessage[];
  isStreaming: boolean;
  isLoading: boolean;
  error: string | null;
  turnSegments: TurnSegment[];
  contentStartedAfterSegmentCount: number | null;
  lastUserMessage?: string;
  conversationId?: string | null;
  draft?: string;
  actionDraftContext?: ActionDraftContext | null;
  draftAttachments?: AssistantDraftAttachment[];
  isActive?: boolean;
  composerFocusRequestKey?: number;
  onSubmitQuery: (query: string, surface: AssistantEntrySurface, action?: { intent: ActionIntent }) => Promise<void>;
  onRetryRecovery?: (surface: AssistantEntrySurface) => Promise<void>;
  onStop: () => void;
  onClear: () => void;
  onFeedback: (turnId: string, feedback: AssistantFeedback | null, surface: AssistantEntrySurface) => Promise<void>;
  onDraftChange?: (draft: string) => void;
  onActionDraftContextChange?: (context: ActionDraftContext | null) => void;
  onAddAttachments?: (files: File[]) => Promise<void>;
  onRemoveAttachment?: (clientId: string) => void;
  onRetryAttachment?: (clientId: string) => Promise<void>;
  onExpand?: () => void;
}

/**
 * Assistant Chat
 */
export const AssistantChat = ({
  initialQuery,
  sendInitialQuery = false,
  onInitialQueryConsumed,
  surface,
  artifactState,
  actionState = emptyActionArtifactState(),
  panelActionFamilyId = null,
  onOpenArtifact,
  onOpenAction = () => undefined,
  onStageAction = () => undefined,
  messages,
  isStreaming,
  isLoading,
  error,
  turnSegments,
  contentStartedAfterSegmentCount,
  conversationId = null,
  draft = "",
  actionDraftContext = null,
  draftAttachments = [],
  isActive,
  composerFocusRequestKey = 0,
  onSubmitQuery,
  onRetryRecovery = async () => undefined,
  onStop,
  onClear,
  onFeedback,
  onDraftChange = () => undefined,
  onActionDraftContextChange = () => undefined,
  onAddAttachments = async () => undefined,
  onRemoveAttachment = () => undefined,
  onRetryAttachment = async () => undefined,
  onExpand,
}: AssistantChatProps) => {
  const hasProcessedInitialQuery = useRef(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const openFileDialogRef = useRef<(() => void) | null>(null);
  const draftRef = useRef("");
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false);
  const [isVoiceBusy, setIsVoiceBusy] = useState(false);
  const form = useForm<ChatFormType>({
    initialValues: { message: draft },
    validate: zodResolver(chatFormSchema),
  });

  const hasMessages = messages.length > 0;
  // A pending, bound review invites the person's own word as the approval turn.
  const hasPendingReview =
    surface === "assistant_page" &&
    Object.values(actionState.artifacts).some(artifact => artifact.status === "PENDING" && artifact.binding !== undefined);
  const voiceEntityRefs = useMemo(() => assistantVoiceEntityRefs(artifactState), [artifactState]);
  draftRef.current = form.values.message;

  useEffect(() => {
    if (draft !== form.values.message) {
      form.setFieldValue("message", draft);
      draftRef.current = draft;
    }
  }, [draft, form]);

  // ========== SCROLL BEHAVIOR ==========

  /** Check if we're scrolled near the bottom */
  const isNearBottom = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return true;
    }
    const threshold = 100; // pixels from bottom
    return viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < threshold;
  }, []);

  /** Scroll to bottom of chat */
  const scrollToBottom = useCallback((smooth = false) => {
    const viewport = viewportRef.current;
    if (viewport) {
      viewport.scrollTo({
        top: viewport.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
      setShowScrollButton(false);
      setIsUserScrolledUp(false);
    }
  }, []);

  /** Handle user scroll - detect if they scrolled up */
  const handleScroll = useCallback(() => {
    const nearBottom = isNearBottom();
    setShowScrollButton(!nearBottom);
    setIsUserScrolledUp(!nearBottom);
  }, [isNearBottom]);

  // Auto-scroll when messages change (unless user scrolled up)
  useEffect(() => {
    if (!isUserScrolledUp) {
      scrollToBottom();
    }
  }, [messages, isUserScrolledUp, scrollToBottom]);

  // Auto-scroll while the turn works — the thinking line and activity chips grow the transcript
  // just like content does (unless user scrolled up)
  useEffect(() => {
    if (!isUserScrolledUp && (isLoading || isStreaming)) {
      scrollToBottom();
    }
  }, [isLoading, isStreaming, turnSegments, isUserScrolledUp, scrollToBottom]);

  // ========== INPUT FOCUS ==========

  /** Focus the input field */
  const focusInput = useCallback(() => {
    setTimeout(() => textareaRef.current?.focus(), 50);
  }, []);

  useEffect(() => {
    if (composerFocusRequestKey > 0) {
      focusInput();
    }
  }, [composerFocusRequestKey, focusInput]);

  // The compact surface stays mounted while closed, so every explicit open must
  // renew focus after its transition. The full page retains the initial empty-state focus.
  useEffect(() => {
    if (isActive === false || (hasMessages && isActive !== true)) {
      return;
    }
    const timeout = window.setTimeout(() => textareaRef.current?.focus(), isActive ? 180 : 50);
    return () => window.clearTimeout(timeout);
  }, [hasMessages, isActive]);

  const submitQuery = useCallback(
    async (query: string) => {
      setIsUserScrolledUp(false);
      await onSubmitQuery(query, surface);
      setTimeout(() => scrollToBottom(), 0);
    },
    [onSubmitQuery, scrollToBottom, surface],
  );

  const handleRetry = useCallback(() => {
    void onRetryRecovery(surface);
  }, [onRetryRecovery, surface]);

  const handleSubmit = async (values: ChatFormType) => {
    if (isVoiceBusy) {
      return;
    }
    await submitQuery(values.message);
  };

  const handlePromptClick = useCallback(
    async (prompt: string) => {
      await submitQuery(prompt);
    },
    [submitQuery],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (
        (form.values.message.trim() || draftAttachments.length > 0) &&
        draftAttachments.every(attachment => attachment.status === "attached") &&
        !isStreaming &&
        !isLoading &&
        !isVoiceBusy
      ) {
        handleSubmit(form.values);
      }
    }
  };

  const handleVoiceTranscript = useCallback(
    (transcript: string) => {
      const nextDraft = appendVoiceTranscript(draftRef.current, transcript);
      draftRef.current = nextDraft;
      form.setFieldValue("message", nextDraft);
      onDraftChange(nextDraft);
      focusInput();
    },
    [focusInput, form, onDraftChange],
  );

  // A user stop aborts the request without a completion or error event, so the in-flight UI
  // state settles here, the same way it does on complete/error: collapse whatever was
  // accomplished into the message instead of discarding it.
  const handleStop = () => {
    onStop();
    focusInput();
  };

  const handleClear = () => {
    setShowScrollButton(false);
    setIsUserScrolledUp(false);
    onClear();
    onDraftChange("");
    onActionDraftContextChange(null);
    focusInput();
  };

  const handleChangeAction = useCallback(
    (target: Omit<ActionDraftContext, "mode">) => {
      onActionDraftContextChange({ ...target, mode: "change" });
      focusInput();
    },
    [focusInput, onActionDraftContextChange],
  );

  // "Correct this cost" seeds the composer, never a turn: the person types the corrected value
  // and the model prepares an in-place correction of the exact saved entry (canvas receipt board).
  const handleCorrectAction = useCallback(
    (target: Omit<ActionDraftContext, "mode">) => {
      onActionDraftContextChange({ ...target, mode: "correct" });
      focusInput();
    },
    [focusInput, onActionDraftContextChange],
  );

  useEffect(() => {
    if (!initialQuery) {
      return;
    }
    const disposition = initialQueryDisposition(initialQuery, sendInitialQuery, hasProcessedInitialQuery.current);
    if (disposition === "none") {
      return;
    }
    hasProcessedInitialQuery.current = true;
    if (disposition === "send") {
      void submitQuery(initialQuery);
      onInitialQueryConsumed?.();
      return;
    }
    form.setFieldValue("message", initialQuery);
    onDraftChange(initialQuery);
    onInitialQueryConsumed?.();
  }, [initialQuery, sendInitialQuery, onInitialQueryConsumed, form, onDraftChange, submitQuery]);

  const lastMessage = messages[messages.length - 1];
  const inFlightReply = lastMessage?.role === "assistant" && (isStreaming || turnSegments.length > 0) ? lastMessage : null;
  const settledMessages = inFlightReply ? messages.slice(0, -1) : messages;
  const liveSegments = splitTurnSegmentsAroundContent(turnSegments, contentStartedAfterSegmentCount);
  const liveTurnFrontier = resolveLiveTurnFrontier(Boolean(inFlightReply), liveSegments.afterContent.length > 0);
  const attachmentsReady = draftAttachments.every(attachment => attachment.status === "attached");

  return (
    <Flex direction="column" h="100%" gap="md" style={{ ...attachmentColors, minWidth: 0 }}>
      {/* Header */}
      <Flex justify="space-between" align="center" pb="sm">
        <Flex align="center" gap="sm">
          <ThemeIcon size={28} variant="light" color="blue" radius="md">
            <IconSparkles size={16} />
          </ThemeIcon>
          <Text fw={600} size="lg" c="gray.8">
            Assistant
          </Text>
        </Flex>
        <Flex align="center" gap={4}>
          {onExpand && (
            <ActionIcon
              className={assistantStyles.mobileTouchTarget}
              aria-label="Open full screen"
              variant="subtle"
              color="gray"
              onClick={() => onExpand()}
              title="Open full screen"
            >
              <IconArrowsMaximize size={16} />
            </ActionIcon>
          )}
          {hasMessages && (
            <ActionIcon
              className={assistantStyles.mobileTouchTarget}
              aria-label="Start over"
              variant="subtle"
              color="gray"
              onClick={handleClear}
              disabled={isStreaming || isLoading}
              title="Start over"
            >
              <IconTrash size={16} />
            </ActionIcon>
          )}
        </Flex>
      </Flex>
      {/* Messages with scroll container */}
      <Box style={{ flex: 1, position: "relative", overflow: "hidden" }}>
        <style>{`
          .assistant-thread [data-radix-scroll-area-viewport] > div {
            display: block !important;
          }
        `}</style>
        <ScrollArea className="assistant-thread" viewportRef={viewportRef} h="100%" offsetScrollbars onScrollPositionChange={handleScroll}>
          <Flex direction="column" gap="sm" pb="md" pr="xs">
            {!hasMessages && !isLoading && (
              <WelcomeState
                onPromptClick={handlePromptClick}
                isDisabled={isStreaming || isLoading || isVoiceBusy}
                compact={surface === "ask_ai_popover"}
              />
            )}
            {settledMessages.map((msg, idx) => (
              <MessageBubble
                key={idx}
                message={msg}
                artifactState={artifactState}
                actionState={actionState}
                panelActionFamilyId={panelActionFamilyId}
                onOpenArtifact={onOpenArtifact}
                onOpenAction={onOpenAction}
                onStageAction={onStageAction}
                onSubmitAction={(query, intent) => onSubmitQuery(query, surface, { intent })}
                onSubmitClarification={query => onSubmitQuery(query, surface)}
                onChangeAction={handleChangeAction}
                onCorrectAction={handleCorrectAction}
                conversationId={conversationId}
                onFeedback={feedback => (msg.turnId ? onFeedback(msg.turnId, feedback, surface) : Promise.resolve())}
                showActions
                compact={surface === "ask_ai_popover"}
              />
            ))}
            {liveSegments.beforeContent.length > 0 && (
              <LiveTurnSegmentTrace segments={liveSegments.beforeContent} liveFrontier={liveTurnFrontier === "beforeContent"} />
            )}
            {(isLoading || isStreaming) && turnSegments.length === 0 && liveTurnFrontier === "beforeContent" && (
              <Box pl={4} role="status" aria-live="polite">
                <AgentThinkingLine label="Thinking…" />
              </Box>
            )}
            {inFlightReply && (
              <MessageBubble
                key={messages.length - 1}
                message={inFlightReply}
                artifactState={artifactState}
                actionState={actionState}
                panelActionFamilyId={panelActionFamilyId}
                onOpenArtifact={onOpenArtifact}
                onOpenAction={onOpenAction}
                onStageAction={onStageAction}
                onSubmitAction={(query, intent) => onSubmitQuery(query, surface, { intent })}
                onSubmitClarification={query => onSubmitQuery(query, surface)}
                onChangeAction={handleChangeAction}
                onCorrectAction={handleCorrectAction}
                conversationId={conversationId}
                liveFrontier={liveTurnFrontier === "answer"}
                compact={surface === "ask_ai_popover"}
              />
            )}
            {liveSegments.afterContent.length > 0 && (
              <LiveTurnSegmentTrace segments={liveSegments.afterContent} liveFrontier={liveTurnFrontier === "afterContent"} />
            )}
            {error && <ErrorBubble message={error} onRetry={handleRetry} disabled={isStreaming || isLoading} />}
          </Flex>
        </ScrollArea>
        {/* Scroll to bottom button */}
        <Transition mounted={showScrollButton} transition="slide-up" duration={200}>
          {styles => (
            <Box
              style={{
                ...styles,
                position: "absolute",
                bottom: 8,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 10,
              }}
            >
              <ActionIcon
                aria-label="Scroll to latest message"
                size={32}
                radius="xl"
                variant="filled"
                color="gray"
                onClick={() => scrollToBottom(true)}
                style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
              >
                <IconArrowDown size={16} />
              </ActionIcon>
            </Box>
          )}
        </Transition>
      </Box>
      {/* Input */}
      <Dropzone
        activateOnClick={false}
        openRef={openFileDialogRef}
        multiple
        maxFiles={5}
        maxSize={ASSISTANT_ATTACHMENT_MAX_FILE_BYTES}
        disabled={isStreaming || isLoading}
        accept={{
          "application/pdf": [".pdf"],
          "image/png": [".png"],
          "image/jpeg": [".jpg", ".jpeg"],
          "image/webp": [".webp"],
          "text/csv": [".csv"],
          "text/plain": [".txt"],
          "text/markdown": [".md"],
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
        }}
        onDrop={files => void onAddAttachments(files)}
        onReject={rejections => void onAddAttachments(rejections.map(rejection => rejection.file))}
        style={{ marginTop: "auto", padding: 0, border: 0, background: "transparent", position: "relative" }}
      >
        <Dropzone.Accept>
          <Box className={assistantStyles.dropOverlay}>Drop files to attach</Box>
        </Dropzone.Accept>
        <Dropzone.Reject>
          <Box className={assistantStyles.dropOverlay}>That file type or size is not supported</Box>
        </Dropzone.Reject>
        <Box className={assistantStyles.composer} style={{ borderRadius: 14, backgroundColor: "white", overflow: "hidden" }}>
          {draftAttachments.length > 0 && (
            <Flex wrap="wrap" gap={6} px="sm" pt="sm">
              {draftAttachments.map(attachment => (
                <AttachmentChip
                  key={attachment.clientId}
                  fileName={attachment.fileName}
                  sizeBytes={attachment.sizeBytes}
                  status={attachment.status}
                  error={attachment.error}
                  retryable={attachment.retryable}
                  previewUrl={attachment.previewUrl}
                  onRetry={() => void onRetryAttachment(attachment.clientId)}
                  onRemove={() => onRemoveAttachment(attachment.clientId)}
                />
              ))}
            </Flex>
          )}
          <Flex align="center" gap={0}>
            {actionDraftContext && (
              <Flex px="sm" pt="xs" align="center" gap="xs">
                <Text size="xs" fw={600} c="blue.7">
                  {actionDraftContext.mode === "correct" ? `Correct ${actionDraftContext.subject}` : `Change ${actionDraftContext.subject}`}
                </Text>
                <Button size="compact-xs" variant="subtle" color="gray" onClick={() => onActionDraftContextChange(null)}>
                  Remove
                </Button>
              </Flex>
            )}
            <ActionIcon
              aria-label="Attach files"
              ml={8}
              size={44}
              radius="xl"
              variant="subtle"
              color="gray"
              disabled={isStreaming || isLoading}
              onClick={() => openFileDialogRef.current?.()}
            >
              <IconPaperclip size={19} />
            </ActionIcon>
            <Textarea
              ref={textareaRef}
              {...form.getInputProps("message")}
              aria-label="Message"
              placeholder={
                actionDraftContext?.mode === "correct"
                  ? ASSISTANT_ACTION_COPY.correctionPlaceholderFor(actionDraftContext.subject)
                  : actionDraftContext?.mode === "change"
                    ? ASSISTANT_ACTION_COPY.changePlaceholder
                    : hasPendingReview
                      ? ASSISTANT_ACTION_COPY.composerHint
                      : hasMessages
                        ? "Ask a follow-up question..."
                        : "What would you like to know?"
              }
              autosize
              minRows={1}
              maxRows={4}
              disabled={shouldLockAssistantDraft(isStreaming, isLoading, isVoiceBusy)}
              variant="unstyled"
              style={{ flex: 1 }}
              className={
                surface === "ask_ai_popover"
                  ? `${assistantStyles.composerInput} ${assistantStyles.composerInputCompact}`
                  : assistantStyles.composerInput
              }
              onKeyDown={handleKeyDown}
              onPaste={event => {
                const images = [...event.clipboardData.files].filter(file => file.type.startsWith("image/"));
                if (images.length > 0) {
                  if (event.clipboardData.getData("text/plain").length === 0) {
                    event.preventDefault();
                  }
                  void onAddAttachments(images);
                }
              }}
              onChange={event => {
                form.setFieldValue("message", event.currentTarget.value);
                onDraftChange(event.currentTarget.value);
              }}
            />
            <Flex align="center" justify="center" pr={10} style={{ alignSelf: "stretch" }}>
              <style>{`
              @media (prefers-reduced-motion: reduce) {
                .assistant-motion-control {
                  transition: none !important;
                }
              }
            `}</style>
              <AssistantVoiceInput
                disabled={isStreaming || isLoading}
                isActive={isActive}
                entityRefs={voiceEntityRefs}
                onTranscript={handleVoiceTranscript}
                onBusyChange={setIsVoiceBusy}
              />
              {isStreaming || isLoading ? (
                <ActionIcon
                  aria-label="Stop response"
                  className={classNames("assistant-motion-control", assistantStyles.mobileTouchTarget)}
                  size={34}
                  radius="xl"
                  color="gray"
                  variant="filled"
                  onClick={handleStop}
                  style={{ transition: "all 0.15s ease" }}
                  title="Stop"
                >
                  <IconPlayerStopFilled size={16} />
                </ActionIcon>
              ) : (
                <ActionIcon
                  aria-label="Send message"
                  className={classNames("assistant-motion-control", assistantStyles.mobileTouchTarget)}
                  size={34}
                  radius="xl"
                  color="blue"
                  variant={(form.values.message.trim() || draftAttachments.length > 0) && attachmentsReady ? "filled" : "light"}
                  disabled={(!form.values.message.trim() && draftAttachments.length === 0) || !attachmentsReady || isVoiceBusy}
                  onClick={() => handleSubmit(form.values)}
                  style={{ transition: "all 0.15s ease" }}
                >
                  <IconArrowUp size={18} />
                </ActionIcon>
              )}
            </Flex>
          </Flex>
        </Box>
      </Dropzone>
    </Flex>
  );
};

/**
 * Starter prompts, in the operator's own voice ("open book", "biggest customers"), one per
 * shipped governed capability domain: AR aging, sales ranking, open orders, purchase spend,
 * pipeline, notes search. Never ship a chip that maps to an unbuilt capability (churn
 * ranking is the known want; it joins this list when it ships). The first three are the
 * fastest wow-answers and are all the compact popover shows.
 */
const STARTER_PROMPTS = [
  "Who owes us money, and how overdue is it?",
  "Who are our biggest customers this year?",
  "What's our open book by customer right now?",
  "What have we spent with suppliers this quarter?",
  "What's in the pipeline, and what's it worth?",
  "What has my team said about pricing lately?",
] as const;

/**
 * Welcome state. The full page teaches breadth (headline, subline, all six chips); the
 * compact popover is a quick-ask surface and shows only the headline and the top three.
 */
export const WelcomeState = ({
  onPromptClick,
  isDisabled,
  compact = false,
}: {
  onPromptClick: (prompt: string) => void;
  isDisabled: boolean;
  compact?: boolean;
}) => (
  <Flex
    direction="column"
    align="center"
    justify="center"
    gap={compact ? "md" : "xl"}
    px="md"
    style={{ flex: 1, minHeight: compact ? 220 : 340 }}
  >
    <Flex direction="column" align="center" gap={6}>
      <Text size={compact ? "lg" : "xl"} fw={600} c="gray.8">
        Ask about your business
      </Text>
      {!compact && (
        <Text size="sm" c="gray.6" ta="center" maw={440} lh={1.5}>
          Customers, orders, margins, costs, and what your team has noted.
        </Text>
      )}
    </Flex>
    {/* Chips hug their text and wrap, so the cluster stays centered at any width instead
        of stretching with the column. */}
    <Flex wrap="wrap" justify="center" gap="xs" maw={640}>
      {(compact ? STARTER_PROMPTS.slice(0, 3) : STARTER_PROMPTS).map(prompt => (
        <PromptBubble key={prompt} prompt={prompt} onClick={onPromptClick} disabled={isDisabled} />
      ))}
    </Flex>
    {/* Owned here (not the page) so every surface that renders prompts gets identical
        hover, keyboard focus, and reduced-motion behavior. */}
    <style>{`
      .assistant-prompt:hover:not(:disabled) {
        transform: translateX(4px);
        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.18) !important;
      }
      .assistant-prompt:focus-visible {
        outline: 2px solid ${BLUE_PALETTE[5]};
        outline-offset: 2px;
      }
      @media (prefers-reduced-motion: reduce) {
        .assistant-prompt {
          transition: none !important;
        }
        .assistant-prompt:hover:not(:disabled) {
          transform: none;
        }
      }
    `}</style>
  </Flex>
);

/**
 * Starter prompt chip. Deliberately quiet: white pill hugging its text, soft blue shadow,
 * blue text; hover slides and deepens the shadow. No fill or border — filled blue pills
 * read as shouting next to the composer.
 */
const PromptBubble = ({ prompt, onClick, disabled }: { prompt: string; onClick: (prompt: string) => void; disabled?: boolean }) => (
  <Paper
    component="button"
    type="button"
    className="assistant-prompt"
    disabled={disabled}
    px="md"
    py={8}
    radius="xl"
    style={{
      cursor: disabled ? "not-allowed" : "pointer",
      backgroundColor: "white",
      border: 0,
      transition: "all 0.2s ease",
      opacity: disabled ? 0.6 : 1,
      boxShadow: "0 1px 3px rgba(59, 130, 246, 0.08)",
      textAlign: "left",
    }}
    onClick={() => onClick(prompt)}
  >
    <Text c="blue.7" lh={1.5} style={{ fontSize: CHAT_TYPE.compact }}>
      {prompt}
    </Text>
  </Paper>
);

export const TurnSegmentTrace = ({
  segments,
  liveFrontier = false,
  compact = false,
}: {
  segments: TurnSegment[];
  liveFrontier?: boolean;
  compact?: boolean;
}) => (
  <Flex direction="column" gap={4} pl={4}>
    {segments.map((segment, index) => {
      const isFrontier = liveFrontier && index === segments.length - 1;
      return (
        <Box key={segment.id} {...(isFrontier ? { role: "status", "aria-live": "polite", "aria-atomic": "false" } : {})}>
          {segment.kind === "thought" && (
            <Text size={compact ? "xs" : "sm"} c={compact ? "gray.5" : "gray.6"} fs="italic">
              {segment.text}
            </Text>
          )}
          {segment.kind === "phase" &&
            (isFrontier && (segment.phase !== "preparing_attachments" || segment.state === "started") ? (
              <AgentThinkingLine label={segment.label} />
            ) : segment.phase === "preparing_attachments" ? (
              <Flex align="center" gap={4} c={GRAY_PALETTE[6]}>
                {segment.state === "completed" && <IconCheck size={12} aria-label="Read" />}
                {segment.state === "failed" && <IconX size={12} aria-label="Could not read" />}
                <Text size="xs">{segment.label}</Text>
              </Flex>
            ) : (
              <Text size={compact ? "xs" : "sm"} c={compact ? "gray.5" : "gray.6"} fs="italic">
                {segment.label}
              </Text>
            ))}
          {segment.kind === "toolBatch" && <AgentActivityTrace steps={segment.steps} />}
        </Box>
      );
    })}
  </Flex>
);

/**
 * Live view of an in-flight turn: tool activity stays fully visible, but thinking renders as one
 * shimmering ticker line carrying the latest substantive sentence, replaced in place as the model
 * works. The chevron expands the complete interleaved trail (thought prose between tool batches)
 * mid-run for anyone who wants to watch the whole thing; the post-turn WorkSummary expander
 * remains the settled home of that trail. Grammar matches the frontier default of a glanceable
 * animated line with the full stream one click away, instead of prose streaming faster than it
 * can be read.
 */
export const LiveTurnSegmentTrace = ({ segments, liveFrontier }: { segments: TurnSegment[]; liveFrontier: boolean }) => {
  const [expanded, setExpanded] = useState(false);
  const activitySegments = segments.filter(segment => segment.kind !== "thought");
  const frontierKind = segments[segments.length - 1]?.kind;
  const thinkingIsFrontier = frontierKind === "thought";
  // While the phase line ("Reviewing the results…") is the frontier it owns the live affordance;
  // a stale ticker sentence beside it would read as two competing thinking lines.
  const latestLabel = frontierKind === "phase" ? null : (latestThoughtTicker(segments) ?? (thinkingIsFrontier ? "Thinking…" : null));
  const flush = frontierKind === "phase" || !liveFrontier;
  const [heldLabel, setHeldLabel] = useState(latestLabel);
  const lastChangeAtRef = useRef(Date.now());
  const latestLabelRef = useRef(latestLabel);
  latestLabelRef.current = latestLabel;

  useEffect(() => {
    const next = dwellGatedTickerLabel({
      displayedLabel: heldLabel,
      latestLabel: latestLabelRef.current,
      lastChangeAtMs: lastChangeAtRef.current,
      nowMs: Date.now(),
      flush,
    });
    if (next.waitMs === null) {
      if (next.displayedLabel !== heldLabel) {
        lastChangeAtRef.current = next.lastChangeAtMs;
        setHeldLabel(next.displayedLabel);
      }
      return;
    }
    const timeout = window.setTimeout(() => {
      const applied = dwellGatedTickerLabel({
        displayedLabel: heldLabel,
        latestLabel: latestLabelRef.current,
        lastChangeAtMs: lastChangeAtRef.current,
        nowMs: Date.now(),
        flush: false,
      });
      lastChangeAtRef.current = applied.lastChangeAtMs;
      setHeldLabel(applied.displayedLabel);
    }, next.waitMs);
    return () => window.clearTimeout(timeout);
  }, [latestLabel, flush, heldLabel]);

  const tickerLabel = flush ? latestLabel : heldLabel;
  return (
    <Box>
      {expanded ? (
        <TurnSegmentTrace segments={segments} liveFrontier={liveFrontier} />
      ) : (
        activitySegments.length > 0 && <TurnSegmentTrace segments={activitySegments} liveFrontier={liveFrontier && !thinkingIsFrontier} />
      )}
      {tickerLabel !== null && (
        <UnstyledButton
          onClick={() => setExpanded(previous => !previous)}
          aria-expanded={expanded}
          aria-label={expanded ? "Hide the full thinking trail" : "Show the full thinking trail"}
          mt={4}
          pl={4}
          className={assistantStyles.thinkingToggle}
        >
          <IconChevronRight
            size={12}
            color={GRAY_PALETTE[6]}
            style={{ flexShrink: 0, transform: expanded ? "rotate(90deg)" : "none", transition: "transform 0.15s ease" }}
          />
          {/* When expanded, the trail's frontier owns the aria-live announcement — avoid doubling it. */}
          <Box {...(expanded ? {} : { role: "status", "aria-live": "polite", "aria-atomic": "true" })} style={{ minWidth: 0 }}>
            <AgentThinkingLine label={tickerLabel} />
          </Box>
        </UnstyledButton>
      )}
    </Box>
  );
};

/** Collapsed work expander whose open state replays the same ledger around the answer boundary. */
const WorkSummary = ({ work, children }: { work: MessageWork; children: React.ReactNode }) => {
  const [expanded, setExpanded] = useState(false);
  const attachmentPhases = work.segments.filter(segment => segment.kind === "phase" && segment.phase === "preparing_attachments");
  const summarizedWork = {
    ...work,
    segments: work.segments.filter(segment => !(segment.kind === "phase" && segment.phase === "preparing_attachments")),
    contentStartedAfterSegmentCount:
      work.contentStartedAfterSegmentCount === null
        ? null
        : work.contentStartedAfterSegmentCount -
          work.segments
            .slice(0, work.contentStartedAfterSegmentCount)
            .filter(segment => segment.kind === "phase" && segment.phase === "preparing_attachments").length,
  };
  const segments = splitTurnSegmentsAroundContent(summarizedWork.segments, summarizedWork.contentStartedAfterSegmentCount);
  return (
    <Box mb={2} w="100%">
      {attachmentPhases.length > 0 && <TurnSegmentTrace segments={attachmentPhases} compact />}
      <UnstyledButton onClick={() => setExpanded(prev => !prev)} aria-expanded={expanded} className={assistantStyles.workSummaryToggle}>
        <IconChevronRight
          size={12}
          color={GRAY_PALETTE[6]}
          style={{ transform: expanded ? "rotate(90deg)" : "none", transition: "transform 0.15s ease" }}
        />
        <Text size="xs" c="gray.6">
          {formatWorkSummary(work)}
        </Text>
      </UnstyledButton>
      <Collapse in={expanded}>
        <Box pl={12} pt={4}>
          <TurnSegmentTrace segments={segments.beforeContent} compact />
        </Box>
      </Collapse>
      {children}
      <Collapse in={expanded}>
        <Box pl={12} pt={4}>
          <TurnSegmentTrace segments={segments.afterContent} compact />
        </Box>
      </Collapse>
    </Box>
  );
};

const ArtifactAttachment = ({
  artifactKey,
  artifactState,
  onOpenArtifact,
}: {
  artifactKey: string;
  artifactState: ReportArtifactState;
  onOpenArtifact: (artifactKey: string) => void;
}) => {
  const { ref, width } = useElementSize();
  const entry = reportArtifactEntry(artifactState, artifactKey);
  if (!entry) {
    return null;
  }
  const openArtifactKey = entry.kind === "full" ? artifactKey : (artifactState.activeArtifactKey ?? artifactKey);
  const measurementPending = entry.kind === "full" && width <= 0;

  return (
    <Box ref={ref} w="100%" maw="100%" mt={4} mih={measurementPending ? 140 : undefined} style={{ minWidth: 0, overflow: "hidden" }}>
      {measurementPending ? null : entry.kind === "full" && shouldRenderArtifactInline(entry.artifact, inlineGridContentWidth(width)) ? (
        <AssistantInlineResult artifact={entry.artifact} onOpen={() => onOpenArtifact(openArtifactKey)} />
      ) : (
        <AssistantArtifactCard summary={entry.summary} hasDataset={entry.kind === "full"} onOpen={() => onOpenArtifact(openArtifactKey)} />
      )}
    </Box>
  );
};

/**
 * One thread turn. Only the user's message gets a bubble; the assistant's answer is flat
 * prose on the page background (the frontier chat grammar: the answer IS the page, the
 * user's words are quiet reference).
 */
export const MessageBubble = ({
  message,
  artifactState,
  actionState = emptyActionArtifactState(),
  panelActionFamilyId = null,
  onOpenArtifact,
  onOpenAction = () => undefined,
  onStageAction = () => undefined,
  onSubmitAction = () => Promise.resolve(),
  onSubmitClarification = () => Promise.resolve(),
  onChangeAction,
  onCorrectAction,
  showActions = false,
  onFeedback,
  liveFrontier = false,
  compact = false,
  conversationId,
}: {
  message: ChatMessage;
  artifactState: ReportArtifactState;
  actionState?: ActionArtifactState;
  /** The family of the review open in the side pane, whose inline card then shows no controls. */
  panelActionFamilyId?: string | null;
  onOpenArtifact: (artifactKey: string) => void;
  onOpenAction?: (proposalId: string) => void;
  onStageAction?: (proposalId: string, patch: ActionSelectionPatch) => void;
  onSubmitAction?: (query: string, intent: ActionIntent) => Promise<void>;
  onSubmitClarification?: (query: string) => Promise<void>;
  onChangeAction?: (target: Omit<ActionDraftContext, "mode">) => void;
  onCorrectAction?: (target: Omit<ActionDraftContext, "mode">) => void;
  showActions?: boolean;
  onFeedback?: (feedback: AssistantFeedback | null) => Promise<void>;
  liveFrontier?: boolean;
  compact?: boolean;
  conversationId?: string | null;
}) => {
  const isUser = message.role === "user";
  const actionArtifact = message.actionProposalId ? actionState.artifacts[message.actionProposalId] : undefined;
  const latestFamilyArtifact = actionArtifact ? actionState.artifacts[actionState.families[actionArtifact.familyId] ?? ""] : undefined;
  const previewItems = useActionPreviewItems(compact ? null : (conversationId ?? null), actionArtifact);
  const answerBubble = isUser ? (
    <Flex direction="column" align="flex-end" gap={6} maw="75%">
      {message.attachments && message.attachments.length > 0 && (
        <Flex wrap="wrap" justify="flex-end" gap={5}>
          {message.attachments.map((attachment, index) => (
            <AttachmentChip
              key={`${attachment.sourceId}:${index}`}
              fileName={attachment.fileName}
              sizeBytes={attachment.sizeBytes}
              status="attached"
              previewUrl={attachment.previewUrl}
            />
          ))}
        </Flex>
      )}
      <Paper px="md" py="sm" radius="lg" bg="blue.6" style={{ color: "white" }}>
        <Text style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, fontSize: CHAT_TYPE.compact }}>{message.content}</Text>
      </Paper>
    </Flex>
  ) : (
    <Box
      w="100%"
      py={4}
      style={{ fontSize: compact ? CHAT_TYPE.compact : CHAT_TYPE.prose }}
      {...(liveFrontier ? { role: "status", "aria-live": "polite", "aria-atomic": "false" } : {})}
    >
      <AssistantMessageBody>{message.content}</AssistantMessageBody>
    </Box>
  );

  return (
    <Transition mounted transition="fade" duration={150}>
      {styles => (
        <Flex
          className={
            !isUser
              ? classNames(
                  "assistant-message",
                  assistantStyles.assistantMessage,
                  message.feedback ? assistantStyles.hasFeedback : undefined,
                )
              : undefined
          }
          direction="column"
          align={isUser ? "flex-end" : "flex-start"}
          gap={2}
          w={isUser ? undefined : "100%"}
          style={styles}
        >
          {!isUser && message.work ? <WorkSummary work={message.work}>{answerBubble}</WorkSummary> : answerBubble}
          {!isUser && showActions && message.content.trim() && (
            <Flex className="assistant-message-actions" pl={4} gap={2} align="center">
              <CopyAnswerButton message={message.content} />
              {message.turnId && onFeedback && (
                <>
                  <ActionIcon
                    aria-label={message.feedback === "positive" ? "Clear helpful rating" : "Rate this answer helpful"}
                    aria-pressed={message.feedback === "positive"}
                    size="sm"
                    variant={message.feedback === "positive" ? "light" : "subtle"}
                    color={message.feedback === "positive" ? "green" : "gray"}
                    disabled={message.feedbackPending}
                    onClick={() => void onFeedback(nextAssistantFeedback(message.feedback, "positive"))}
                  >
                    <IconThumbUp size={14} />
                  </ActionIcon>
                  <ActionIcon
                    aria-label={message.feedback === "negative" ? "Clear not helpful rating" : "Rate this answer not helpful"}
                    aria-pressed={message.feedback === "negative"}
                    size="sm"
                    variant={message.feedback === "negative" ? "light" : "subtle"}
                    color={message.feedback === "negative" ? "red" : "gray"}
                    disabled={message.feedbackPending}
                    onClick={() => void onFeedback(nextAssistantFeedback(message.feedback, "negative"))}
                  >
                    <IconThumbDown size={14} />
                  </ActionIcon>
                </>
              )}
            </Flex>
          )}
          {!isUser && message.feedbackError && (
            <Text size="xs" c="red.7" pl={4} role="alert">
              {message.feedbackError}
            </Text>
          )}
          {!isUser && visibleDisposition(message.disposition ?? null) && (
            <Text size="sm" c="gray.6" mt={2} role="status" aria-live="polite">
              {visibleDisposition(message.disposition ?? null)}
            </Text>
          )}
          {!isUser && message.commitRejected && (
            <Text size="sm" c="orange.8" mt={2} role="alert">
              {reportCommitRejectionCopy(artifactState.activeArtifactKey !== null)}
            </Text>
          )}
          {!isUser && message.artifactKey && (
            <ArtifactAttachment artifactKey={message.artifactKey} artifactState={artifactState} onOpenArtifact={onOpenArtifact} />
          )}
          {!isUser && message.actionProposalId && actionArtifact && (
            <Box w="100%" mt={4}>
              <ActionProposalCard
                artifact={actionArtifact}
                execution={actionState.execution[message.actionProposalId]}
                controlError={actionState.controlErrors?.[message.actionProposalId]}
                previewItems={previewItems}
                patch={actionState.stagedSelectionPatches[message.actionProposalId] ?? { includeItemKeys: [], excludeItemKeys: [] }}
                surface={compact ? "ask_ai_popover" : "assistant_page"}
                onStage={patch => onStageAction(message.actionProposalId ?? "", patch)}
                onSubmitAction={onSubmitAction}
                onOpenReview={() => onOpenAction(message.actionProposalId ?? "")}
                onChange={
                  conversationId
                    ? () =>
                        onChangeAction?.({
                          conversationId,
                          proposalId: actionArtifact.proposalId,
                          subject: actionArtifact.subject,
                        })
                    : undefined
                }
                onCorrect={item => {
                  if (conversationId && item.resultEntityId) {
                    onCorrectAction?.({
                      conversationId,
                      proposalId: actionArtifact.proposalId,
                      itemKey: item.itemKey,
                      resultEntityId: item.resultEntityId,
                      subject: item.row.subject,
                    });
                  }
                }}
                onClarify={onSubmitClarification}
                paneOpen={!compact && panelActionFamilyId !== null && panelActionFamilyId === actionArtifact.familyId}
                replacementVersion={
                  latestFamilyArtifact && latestFamilyArtifact.version > actionArtifact.version ? latestFamilyArtifact.version : undefined
                }
              />
            </Box>
          )}
        </Flex>
      )}
    </Transition>
  );
};

/**
 * In-thread error bubble: the server's error message verbatim, with a Try again button that
 * resubmits the last user message (plan §5 ERROR_RETRY_COPY). No local rewriting of the copy.
 */
const ErrorBubble = ({ message, onRetry, disabled }: { message: string; onRetry: () => void; disabled: boolean }) => (
  <Flex direction="column" align="flex-start" gap={6}>
    <Paper px="md" py="sm" radius="lg" maw="90%" bg="red.0" style={{ border: `1px solid ${DANGER_PALETTE[2]}` }}>
      <Text size="sm" c="red.8">
        {message}
      </Text>
    </Paper>
    <Button size="xs" variant="light" color="red" onClick={onRetry} disabled={disabled}>
      Try again
    </Button>
  </Flex>
);
